import React, { useCallback, useState } from 'react'
import { BallotBoxIcon } from '@/atoms/Icons/BallotBoxIcon'
import { NewsIcon } from '@/atoms/Icons/NewsIcon'
import { UsersThreeIcon } from '@/atoms/Icons/UsersThreeIcon'
import { CaptionMD, HeadingMD, ParagraphSM } from '@/atoms/Text'
import { useSafeAnalytics } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { TorchSubmitButton } from '../TorchSubmitButton'
import { AccordionSection } from './AccordionSection'
import { RuleSubsection } from './RuleSubsection'
import { useSubmissionRules } from './submissionRules'

export const SubmissionRulesSection: React.FC = () => {
  const { track } = useSafeAnalytics()
  const { t } = useTranslate('create')
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    general: true,
    kids: true,
    unbiased: true,
  })

  const { allSubmissionsRules, episodeRules, torchRules, teenagerRules, youngerKidsRules, unbiasedRules } =
    useSubmissionRules()

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const handleGetStartedClicked = useCallback(() => {
    track('Guild Submit Project Clicked')
  }, [track])

  return (
    <section
      className="w-full py-16 md:py-24"
      style={{
        backgroundImage: 'linear-gradient(130deg, #232323 0%, #141414 100%)',
      }}
    >
      <div className="mx-auto max-w-[1440px] px-4 md:px-12 lg:px-[120px]">
        <HeadingMD weight="bold" color="white" className="md:photon-heading-xl lg:photon-display mb-4 text-center">
          {t('rulesForSubmissions', 'Rules For Submissions')}
        </HeadingMD>

        <ParagraphSM weight="normal" color="core-gray-400" className="mx-auto mb-12 max-w-2xl text-center">
          {t(
            'rulesDescription',
            'These rules are in place to ensure an unbiased voting process and to meet the content expectations of our Guild voters.',
          )}
        </ParagraphSM>

        <div className="mb-12 space-y-6">
          <AccordionSection
            title={t('generalRules', 'General Rules')}
            icon={<NewsIcon size={48} color="white" />}
            isOpen={openSections.general ?? false}
            onToggle={() => toggleSection('general')}
          >
            <RuleSubsection title={t('allSubmissions', 'All Submissions')} rules={allSubmissionsRules} />
            <RuleSubsection title={t('episodes', 'Episodes')} rules={episodeRules} />
            <RuleSubsection title={t('torches', 'Torches')} rules={torchRules} />
          </AccordionSection>

          <AccordionSection
            title={t('submissionsForKids', 'Submissions for Kids')}
            icon={<UsersThreeIcon size={48} color="white" />}
            isOpen={openSections.kids ?? false}
            onToggle={() => toggleSection('kids')}
          >
            <RuleSubsection title={t('teenagers', 'Teenagers')} rules={teenagerRules} />
            <RuleSubsection title={t('youngerKids', 'Younger Kids')} rules={youngerKidsRules} />
          </AccordionSection>

          <AccordionSection
            title={t('keepingSubmissionsUnbiased', 'Keeping Submissions Unbiased')}
            icon={<BallotBoxIcon size={48} color="white" />}
            isOpen={openSections.unbiased ?? false}
            onToggle={() => toggleSection('unbiased')}
          >
            <RuleSubsection title={''} rules={unbiasedRules} />
          </AccordionSection>
        </div>

        <div className="flex flex-col items-center gap-4">
          <TorchSubmitButton handleSubmitClicked={handleGetStartedClicked} isDark={true} />
          <CaptionMD weight="normal" color="core-gray-500">
            {t('applicationFee', '$250 application fee')}
          </CaptionMD>
        </div>
      </div>
    </section>
  )
}
